import * as React from 'react';
import { GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';
import { COLORS } from '@/constants/colors';
import { roundedRect, drawCancelIcon, BOX_HEIGHT, BOX_RADIUS } from '@/utils/canvasUtils';

// ─── 레이아웃 상수 ────────────────────────────────────────────────
const RADIUS = BOX_RADIUS;

const W_PRICE_MIN = 60; // 가격 박스 최소 너비
const W_PRICE_PAD = 20; // 가격 박스 좌우 여백 (각 10px)
const W_CLOSE = 24; // ✕

// 최소 너비 (초기값·fallback용)
export const TP_AND_SL_BOX_W = W_PRICE_MIN + W_CLOSE;

const BOX_X_PRICE = 0;
const FONT = 'normal 12px -apple-system, system-ui, sans-serif';

// ─── Props ────────────────────────────────────────────────────────
export interface TPAndSLBoxProps {
  readonly yValue: number;
  readonly type: 'TP' | 'SL';
  readonly positionType: 'long' | 'short';
  readonly priceText: string; // 표시할 가격 문자열 (e.g. "+6.33", "-12.66")
  readonly rightOffset: number;
  readonly draggable: boolean;
  readonly isDark?: boolean;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDelete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class TPAndSLBox extends React.Component<TPAndSLBoxProps> {
  static defaultProps = {
    draggable: false,
    rightOffset: 60,
  };

  public boxX = 0;
  public boxY = 0;
  public totalW = TP_AND_SL_BOX_W;

  render() {
    const { onHover, onUnHover, onDragStart, onDrag, onDragComplete, draggable } = this.props;

    return (
      <GenericChartComponent
        clip={true}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        isHover={this.isHover}
        enableDragOnHover
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onHover={onHover}
        onUnHover={onUnHover}
        onClick={this.handleClick}
        onDragStart={draggable ? onDragStart : undefined}
        onDrag={draggable ? onDrag : undefined}
        onDragComplete={draggable ? onDragComplete : undefined}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    ctx.save();
    const { yValue, type, positionType, priceText, rightOffset, isDark } = this.props;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;

    const y = Math.round(yScale(yValue));
    if (y < 0 || y > height) {
      ctx.restore();
      return;
    }

    // 텍스트 너비에 따라 가격 박스 너비 동적 계산
    ctx.font = FONT;
    const textW = ctx.measureText(priceText).width;
    const wPrice = Math.max(W_PRICE_MIN, Math.ceil(textW + W_PRICE_PAD));
    const boxXClose = BOX_X_PRICE + wPrice;
    const totalW = wPrice + W_CLOSE;

    const posColor = positionType === 'long' ? COLORS.long : COLORS.short;
    const boxX = Math.round(width - totalW - rightOffset - 1); // 핸들 라벨과 1px 겹쳐줘서 틈새 제거
    const boxY = Math.round(y - BOX_HEIGHT / 2);

    this.boxX = boxX;
    this.boxY = boxY;
    this.totalW = totalW;

    const cy = y;
    // TP: 항상 초록(profit) / SL: 항상 빨강(loss). 라벨("TP"/"SL") 표시는 핸들 라벨이 담당
    const priceBoxBgColor = type === 'TP' ? COLORS.tp : COLORS.sl;

    // 1) 전체 배경 (다크: #1f1f1f / 라이트: 흰색, 좌측 radius=0 — 핸들 라벨과 밀착)
    ctx.fillStyle = isDark ? '#1f1f1f' : '#FFFFFF';
    roundedRect(ctx, boxX, boxY, totalW, BOX_HEIGHT, RADIUS, 'right');
    ctx.fill();

    // 2) 가격 박스 배경
    const priceBoxPadding = 3;
    ctx.fillStyle = priceBoxBgColor;
    roundedRect(
      ctx,
      boxX + priceBoxPadding,
      boxY + priceBoxPadding,
      wPrice - priceBoxPadding * 2,
      BOX_HEIGHT - priceBoxPadding * 2,
      RADIUS,
    );
    ctx.fill();

    // 3) 상/우/하 3면 테두리 (좌측은 핸들 라벨이 담당) — fill 이후 마지막에 그림
    ctx.strokeStyle = posColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(boxX, boxY);
    ctx.lineTo(boxX + totalW - RADIUS, boxY);
    ctx.arcTo(boxX + totalW, boxY, boxX + totalW, boxY + RADIUS, RADIUS);
    ctx.lineTo(boxX + totalW, boxY + BOX_HEIGHT - RADIUS);
    ctx.arcTo(boxX + totalW, boxY + BOX_HEIGHT, boxX + totalW - RADIUS, boxY + BOX_HEIGHT, RADIUS);
    ctx.lineTo(boxX, boxY + BOX_HEIGHT);
    ctx.stroke();

    // 가격 텍스트 (흰색)
    const priceCy = boxY + priceBoxPadding + (BOX_HEIGHT - priceBoxPadding * 2) / 2;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(priceText, boxX + BOX_X_PRICE + wPrice / 2, priceCy);

    // X 아이콘
    drawCancelIcon(ctx, boxX + boxXClose + W_CLOSE / 2, cy, posColor);
    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (!onHover) return false;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;
    return (
      mouseX >= this.boxX &&
      mouseX <= this.boxX + this.totalW &&
      mouseY >= this.boxY &&
      mouseY <= this.boxY + BOX_HEIGHT
    );
  };

  // RFC의 GenericChartComponent는 onClick을 isHover 여부와 무관하게 모든 컴포넌트에 전파함.
  // 이 때문에 박스 밖을 클릭해도 onDelete가 호출되어 오더 라인이 삭제되는 버그가 발생했음.
  // 박스 영역 밖 클릭은 직접 걸러내고, X 아이콘 영역 클릭일 때만 onDelete를 호출하도록 해줌.
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;
    // 박스 영역 밖이면 무시
    if (mouseX < this.boxX || mouseX > this.boxX + this.totalW || mouseY < this.boxY || mouseY > this.boxY + BOX_HEIGHT)
      return;
    // X 아이콘 영역(오른쪽 W_CLOSE px) 클릭 시에만 삭제
    const xClose = this.boxX + this.totalW - W_CLOSE;
    if (mouseX >= xClose) {
      this.props.onDelete?.(e, moreProps);
    }
  };
}
